/**
 * «Готово» and «Скасовано» notices: the person who handed work out hears when it is done, and the
 * person doing it hears when it is called off. The server derives them for push and the browser
 * for «Мої завдання» from the same snapshot, so both get the same ids.
 */

type Row = Record<string, unknown>
const record = (value: unknown): Row =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : {}
const rows = (value: unknown): Row[] => (Array.isArray(value) ? value.map(record) : [])
const text = (value: unknown) => (typeof value === 'string' ? value : '')
const ids = (value: unknown) =>
  Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : []

/** Older events never become notices, so the first release does not replay history. */
export const NOTICES_SINCE = '2026-11-02T13:00:00.000Z'
/** A notice stays this long unless the person closes it earlier. */
export const NOTICE_DAYS = 3

export type NoticeLink =
  | { type: 'task'; id: string }
  | { type: 'engineering'; id: string }
  | { type: 'production'; id: string }

export interface Notice {
  id: string
  kind: 'done' | 'cancelled'
  title: string
  context: string
  /** When it happened, ISO. */
  at: string
  /** Who acted, when known. */
  fromId: string | null
  link: NoticeLink
}

/** `state` is the snapshot state as this person receives it. */
export const deriveNotices = (state: unknown, personId: string, now = Date.now()): Notice[] => {
  const source = record(state)
  const since = Math.max(Date.parse(NOTICES_SINCE), now - NOTICE_DAYS * 86_400_000)
  const recent = (at: unknown) => {
    const time = Date.parse(text(at))
    return Number.isFinite(time) && time >= since
  }
  const notices: Notice[] = []

  for (const task of rows(source.tasks)) {
    const id = text(task.id)
    if (task.creatorId === task.assigneeId) continue
    if (task.status === 'completed' && task.creatorId === personId && recent(task.completedAt))
      notices.push({
        id: `notice-task-done-${id}`,
        kind: 'done',
        title: `Виконано: ${text(task.title)}`,
        context: 'Завдання, яке ви доручили',
        at: text(task.completedAt),
        fromId: text(task.assigneeId) || null,
        link: { type: 'task', id },
      })
    if (task.status === 'cancelled' && task.assigneeId === personId && recent(task.cancelledAt))
      notices.push({
        id: `notice-task-cancelled-${id}`,
        kind: 'cancelled',
        title: `Скасовано: ${text(task.title)}`,
        context: 'Завдання більше не потрібне',
        at: text(task.cancelledAt),
        fromId: text(task.creatorId) || null,
        link: { type: 'task', id },
      })
  }

  const engineering = [
    ...rows(source.orders)
      .filter((order) => order.engineering)
      .map((order) => ({ id: text(record(order.deal).id), request: record(order.engineering) })),
    ...rows(source.engineeringJobs).map((job) => ({
      id: text(job.id),
      request: record(job.request),
    })),
  ]
  for (const { id, request } of engineering) {
    const workers = [text(request.engineerId), ...ids(request.helperIds)]
    if (
      request.state === 'done' &&
      request.requestedById === personId &&
      !workers.includes(personId) &&
      recent(request.updatedAt)
    )
      notices.push({
        id: `notice-eng-done-${id}`,
        kind: 'done',
        title: `Готово у конструкторів: ${text(request.productName) || 'завдання'}`,
        context: 'Технологічну карту затверджено',
        at: text(request.updatedAt),
        fromId: text(request.engineerId) || null,
        link: { type: 'engineering', id },
      })
  }
  for (const job of rows(source.engineeringCancelled)) {
    const workers = [text(job.engineerId), ...ids(job.helperIds)]
    if (!workers.includes(personId) || job.requestedById === personId || !recent(job.cancelledAt))
      continue
    notices.push({
      id: `notice-eng-cancelled-${text(job.id)}`,
      kind: 'cancelled',
      title: `Скасовано: ${text(job.productName) || 'завдання конструктора'}`,
      context: 'Завдання конструктора більше не потрібне',
      at: text(job.cancelledAt),
      fromId: text(job.requestedById) || null,
      link: { type: 'engineering', id: text(job.id) },
    })
  }

  for (const rework of rows(source.engineeringReworks)) {
    const id = text(rework.id)
    const order = { type: 'production' as const, id: text(rework.orderId) }
    if (rework.designerId === rework.requestedById || !recent(rework.resolvedAt)) continue
    if (rework.status === 'done' && rework.requestedById === personId)
      notices.push({
        id: `notice-rework-done-${id}`,
        kind: 'done',
        title: `Доопрацьовано: ${text(rework.productName)}`,
        context: `${text(rework.orderNumber)} · ${text(rework.designerName)}`,
        at: text(rework.resolvedAt),
        fromId: text(rework.designerId) || null,
        link: order,
      })
    if (rework.status === 'cancelled' && rework.designerId === personId)
      notices.push({
        id: `notice-rework-cancelled-${id}`,
        kind: 'cancelled',
        title: `Скасовано доопрацювання: ${text(rework.productName)}`,
        context: `${text(rework.orderNumber)} · ${text(rework.actorName)}`,
        at: text(rework.resolvedAt),
        fromId: text(rework.requestedById) || null,
        link: order,
      })
  }

  for (const order of rows(source.internalOrders)) {
    if (order.requestedById !== personId) continue
    const stages = rows(order.stages)
    const flag = stages[0]?.routeFinished
    const finished =
      typeof flag === 'boolean'
        ? flag
        : stages.length > 0 && stages.every((stage) => stage.status === 'done')
    if (!finished) continue
    const at = stages
      .map((stage) => text(stage.updatedAt))
      .sort()
      .at(-1)
    if (!recent(at)) continue
    notices.push({
      id: `notice-production-done-${text(order.id)}`,
      kind: 'done',
      title: `Виготовлено: ${text(order.productName)}`,
      context: `${text(order.number)} · внутрішнє замовлення`,
      at: at ?? '',
      fromId: null,
      link: { type: 'production', id: text(order.id) },
    })
  }

  return notices.sort((left, right) => right.at.localeCompare(left.at))
}
